import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import logo from "../../../assets/images/logo-main.png";
import {
  creditCGTHomesCoins,
  getCGTHomesCoinCreditHistory,
} from "../../../services/Public";
import CustomTable from "../../components/custom-table/CustomTable";
import Loader from "../../components/loader/Loader";
import Loader2 from "../../components/loader/Loader2";
import "./style.css";

const COIN_AMOUNT = 950;

function CGTHomesCoins() {
  const [userId, setUserId] = useState("");
  const [isPending, setIsPending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const columns = useMemo(
    () => [
      { Header: "User ID", accessor: "userId" },
      { Header: "CGT Coins", accessor: "cgtCoins" },
      { Header: "Package", accessor: "packageAmount" },
      { Header: "CGT Homes Account", accessor: "destinationEmail" },
      { Header: "Status", accessor: "status" },
      { Header: "Credited At", accessor: "createdAt" },
    ],
    []
  );

  const fetchHistory = async () => {
    setIsLoading(true);
    try {
      const res = await getCGTHomesCoinCreditHistory();
      const rows = (res.data || []).map((tx) => ({
        userId: tx.userId,
        cgtCoins: Number(tx.cgtCoins).toLocaleString("en-IN"),
        packageAmount: tx.packageAmount === null ? "-" : `$${tx.packageAmount}`,
        destinationEmail: tx.destinationEmail || "-",
        status: tx.status,
        createdAt: tx.createdAt ? new Date(tx.createdAt).toLocaleString() : "-",
      }));
      setHistory(rows);
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to fetch history");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleCredit = async () => {
    const trimmed = userId.trim().toUpperCase();

    if (!trimmed) {
      toast.warn("Please enter your User ID!");
      return;
    }

    if (!/^CGT\d+$/.test(trimmed)) {
      toast.warn("Invalid User ID format. Example: CGT1000000");
      return;
    }

    setIsPending(true);
    setResult(null);
    try {
      const res = await creditCGTHomesCoins(trimmed);
      setResult(res.data);
      toast.success(res.message || "CGT coins credited successfully");
      setUserId("");
      fetchHistory();
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to credit CGT coins");
    }
    setIsPending(false);
  };

  return (
    <div className="cgthc">
      {isPending && <Loader2 />}

      <div className="cgthc-head">
        <img className="cgthc-logo" alt="logo" src={logo} />
        <h1>CGT Homes Coin Credit</h1>
        <p>
          Enter your CryptoGT User ID to receive {COIN_AMOUNT.toLocaleString("en-IN")} CGT
          coins in the CGT Homes account linked to it. Requires an active hybrid
          package ($0, $10, $50 or $100). One claim every 30 days.
        </p>
      </div>

      <div className="cgthc-card">
        <div className="cgthc-card-top">
          <h2>Claim {COIN_AMOUNT.toLocaleString("en-IN")} CGT Coins</h2>
        </div>
        <div className="cgthc-card-body">
          <div className="input-group">
            <label>User ID</label>
            <input
              type="text"
              name="userId"
              placeholder="e.g. CGT1000000"
              value={userId}
              onChange={(e) => setUserId(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && !isPending && handleCredit()}
            />
          </div>
          <div className="button-group">
            <button type="button" onClick={handleCredit} disabled={isPending}>
              {isPending ? "Processing..." : "Get CGT Coins"}
            </button>
          </div>
        </div>
      </div>

      {result && (
        <div className="cgthc-result">
          <h3>{Number(result.cgtCoins).toLocaleString("en-IN")} CGT coins credited</h3>
          <ul>
            <li>
              <span>User ID</span>
              <strong>{result.userId}</strong>
            </li>
            <li>
              <span>CGT Homes Account</span>
              <strong>{result.destinationEmail}</strong>
            </li>
            <li>
              <span>Hybrid Package</span>
              <strong>${result.packageAmount}</strong>
            </li>
            <li>
              <span>Next Claim Available</span>
              <strong>
                {result.nextEligibleAt
                  ? new Date(result.nextEligibleAt).toLocaleDateString()
                  : "-"}
              </strong>
            </li>
          </ul>
        </div>
      )}

      <div className="cgthc-history">
        {isLoading ? (
          <Loader />
        ) : (
          <CustomTable data={history} columns={columns} heading="Credit History" />
        )}
      </div>
    </div>
  );
}

export default CGTHomesCoins;
